import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// Same headers as JstApiService:
// X-Skip-Error-Redirect lets HTTP errors reach .subscribe(error: ...) instead
// of bouncing the user to the global error page (the overlay renders inline
// errors). X-Silent-Request opts out of the global page spinner — the overlay
// shows its own inline parsing/saving spinners.
const SILENT_REDIRECTLESS_HEADERS = new HttpHeaders({
  'X-Skip-Error-Redirect': 'true',
  'X-Silent-Request': 'true',
});

/**
 * One parsed ACE credential. Mirrors the backend AceTranscriptItem
 * (apps/cpd-subgraph/src/app/ai/parse-ace/parse-ace.schema.ts) — keep in sync
 * if that schema's display fields change. `aceCode` is null for credentials
 * that aren't ACE-endorsed (shown but earn no credit).
 */
export interface AceCredentialRow {
  aceCode?: string | null;
  versionNumber?: number | null;
  credentialTitle?: string | null;
  issuerName?: string | null;
  issuedDate?: string | null;
  grade?: string | null;
}

export interface AceParsedData {
  items: AceCredentialRow[];
}

export interface AceDryRunResponse {
  success: boolean;
  message: string;
  ace_upload_id: number;
  data: AceParsedData;
}

export interface AceConfirmResponse {
  success: boolean;
  ace_upload_id: number;
}

/** One previously-confirmed transcript in the multiplicity read-back. */
export interface AceTranscriptSummary {
  ace_upload_id: number;
  uploaded_at: string;
  data: AceParsedData;
}

export interface AceTranscriptsResponse {
  success: boolean;
  transcripts: AceTranscriptSummary[];
}

@Injectable({
  providedIn: 'root',
})
export class AceApiService {
  // The auth interceptor prepends environment.apiUrlBase to relative URLs,
  // so paths here are relative to /api/.
  private readonly baseUrl = 'ace';

  constructor(private readonly http: HttpClient) {}

  public uploadDryRun(file: File): Observable<AceDryRunResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AceDryRunResponse>(
      `${this.baseUrl}/upload?dryRun=true`,
      formData,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  public confirm(
    aceUploadId: number,
    confirmedData: AceParsedData
  ): Observable<AceConfirmResponse> {
    return this.http.post<AceConfirmResponse>(
      `${this.baseUrl}/confirm`,
      {
        ace_upload_id: aceUploadId,
        confirmed_data: confirmedData,
      },
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  public listTranscripts(): Observable<AceTranscriptsResponse> {
    return this.http.get<AceTranscriptsResponse>(`${this.baseUrl}/transcripts`, {
      headers: SILENT_REDIRECTLESS_HEADERS,
    });
  }
}
